import React, { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  checkPermissions,
  Customer,
  CustomerContext,
  GeoLocationCoords,
  MediaContext,
  TourContext,
  UserContext
} from 'ui-smartforce-settings';
import { FullReportContext } from '../../../Context/FullReportContext';
import {
  FieldValueType,
  ReportTag,
  ReportTags,
  ReportValue
} from '../../../Models';
import { canAmendReport } from '../../../Helpers';
import { ReportHistoryItem } from '../ReportHistory/ReportHistory';
import {
  changeReportableStatus,
  getReport,
  updateReportTags
} from '../../../Services/ReportService';
import { dispatchMainAlertEvent } from '../../../Events/MainAlertEvent';
import { ReportListItem } from '../../ReportList/ReportList';
import { ReportableStatusValue } from '../ReportableStatus/ReportableStatus';
import { isStateReportable } from '../Helpers';
import {
  CadSummaryType,
  ContactSummaryType
} from '../ContactMap/ReportSummaryCard/ReportSummaryCard';
import { USER_REPORTS_TAGS_UPDATE } from '../../../Constants/RolesAndPermissions';

export interface UseReportCardProps {
  report: ReportListItem;
  showAmend: boolean;
  onUpdateReportableStatus: (report: ReportValue) => void;
}

export interface UseReportCardReturnType {
  isHistoryOpen: boolean;
  isLoading: boolean;
  isLargeScreen: boolean;
  reportActive: ReportValue;
  selectedItem: number;
  isAmendDisabled: boolean;
  isSaveButtonDisabled: boolean;
  isChangeTagsDisabled: boolean;
  showReportableStatusModal: boolean;
  showChangeTagsModal: boolean;
  reportableStatus: ReportableStatusValue;
  updatedTags: ReportTags | undefined;
  mapCoords: GeoLocationCoords | undefined;
  contactSummaryValue: ContactSummaryType;
  cadSummaryValue: CadSummaryType | null;
  areasName: string[] | undefined;
  customer: Customer;
  onViewHistory: () => void;
  onReportHistoryClick: (item: ReportHistoryItem, index: number) => void;
  onAmend: () => void;
  onSeeFullReport: () => void;
  onHistoryPanelClose: () => void;
  onHistoryPanelBack: () => (() => void) | undefined;
  onHistoryPanelSave: () => (() => void) | undefined;
  onReportableStatusButtonClick: () => void;
  onReportableStatusChange: (status: ReportableStatusValue) => void;
  onReportableStatusDiscard: () => void;
  onReportableStatusSave: () => void;
  onChangeTagsButtonClick: () => void;
  onTagsDiscard: () => void;
  onTagsUpdate: () => void;
  onTagsChange: (tags: FieldValueType) => void;
}

export const useReportCard = ({
  report,
  showAmend,
  onUpdateReportableStatus
}: UseReportCardProps): UseReportCardReturnType => {
  const location = useLocation();
  const history = useHistory();
  const { isLargeScreen } = React.useContext(MediaContext);
  const { showFullReport } = useContext(FullReportContext);
  const { user } = useContext(UserContext);
  const customer = useContext(CustomerContext).customer as Customer;
  const {
    tour,
    step: tourStep,
    onClose: onTourClose
  } = useContext(TourContext);
  const [isHistoryOpen, setIsHistoryOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [reportActive, setReportActive] = React.useState<ReportValue>(report);
  const [selectedItem, setSelectedItem] = React.useState<number>(0);
  const [showReportableStatusModal, setShowReportableStatusModal] =
    React.useState<boolean>(false);
  const [showChangeTagsModal, setShowChangeTagsModal] =
    React.useState<boolean>(false);

  const [reportableStatus, setReportableStatus] =
    React.useState<ReportableStatusValue>(
      isStateReportable(report as ReportValue) ? 'reportable' : 'not-reportable'
    );

  const intialReportableStatus = React.useRef<ReportableStatusValue>(
    isStateReportable(report as ReportValue) ? 'reportable' : 'not-reportable'
  );

  const [updatedTags, setUpdatedTags] = React.useState<ReportTags | undefined>(
    report?.tags ?? undefined
  );

  useEffect(() => {
    if (
      !isLargeScreen &&
      report.tooltipType &&
      (tour?.id === 6 || tour?.id === 10) &&
      tourStep === 1
    ) {
      setIsHistoryOpen(true);
    }
  }, [isLargeScreen, tour, tourStep, report.tooltipType]);

  useEffect(() => {
    const reportableStatusUpdated = isStateReportable(report as ReportValue)
      ? 'reportable'
      : 'not-reportable';
    setReportActive(report);
    setReportableStatus(reportableStatusUpdated);
    intialReportableStatus.current = reportableStatusUpdated;
  }, [report]);

  const onViewHistory = () => {
    setIsHistoryOpen(true);
  };

  const resetReportableStatus = () => {
    setReportableStatus(intialReportableStatus.current);
  };

  const onReportHistoryClick = async (
    item: ReportHistoryItem,
    index: number
  ) => {
    if (
      report.versions &&
      report.versions?.length > 1 &&
      index !== selectedItem
    ) {
      onTourClose([
        { tourId: 6, step: 1 },
        { tourId: 10, step: 1 }
      ]);
      setIsHistoryOpen(false);
      setIsLoading(true);

      try {
        const versionCreatedAt: string = report.versions[index].created_at;

        const reportActive = await getReport(
          report.general_information.incident_number as string,
          versionCreatedAt
        );

        setSelectedItem(index);
        setReportActive(reportActive);
        setIsLoading(false);
      } catch (e) {
        dispatchMainAlertEvent({
          message:
            'The selected version could not be loaded correctly. Please try again.',
          autoHideDuration: 3600
        });

        setIsLoading(false);
      }
    }
  };

  const onAmend = () => {
    history.push('/app/amend', {
      report,
      redirectTo: location.pathname
    });
  };

  const onSeeFullReport = () => {
    showFullReport(reportActive);
  };

  const onHistoryPanelClose = () => {
    onTourClose([
      { tourId: 6, step: 1 },
      { tourId: 10, step: 1 }
    ]);
    setIsHistoryOpen(false);
    setShowReportableStatusModal(false);
    setShowChangeTagsModal(false);
    resetReportableStatus();
    setUpdatedTags(reportActive.tags ?? undefined);
  };

  const onHistoryPanelBack = () => {
    return showReportableStatusModal
      ? () => {
          setShowReportableStatusModal(false);
          resetReportableStatus();
        }
      : showChangeTagsModal
        ? () => {
            setShowChangeTagsModal(false);
            setUpdatedTags(reportActive.tags ?? undefined);
          }
        : undefined;
  };

  const onHistoryPanelSave = () => {
    return showReportableStatusModal
      ? onReportableStatusSave
      : showChangeTagsModal
        ? onTagsUpdate
        : undefined;
  };

  const onReportableStatusButtonClick = () => {
    setShowReportableStatusModal(true);
  };

  const onReportableStatusChange = (value: ReportableStatusValue) => {
    setReportableStatus(value);
  };

  const onReportableStatusDiscard = () => {
    setShowReportableStatusModal(false);
    resetReportableStatus();
  };

  const onReportableStatusSave = async () => {
    setIsLoading(true);
    try {
      const updatedReport = await changeReportableStatus(
        reportActive.general_information.incident_number as string,
        reportableStatus === 'reportable' ? true : false
      );

      onUpdateReportableStatus(updatedReport);
      setShowReportableStatusModal(false);
      setIsLoading(false);
      dispatchMainAlertEvent({
        message: 'Reportable status updated successfully',
        autoHideDuration: 3600
      });
    } catch (error) {
      console.error('ReportCard::Error saving reportable status:', error);
      setIsLoading(false);
      dispatchMainAlertEvent({
        message: 'The reportable status could not be updated correctly.',
        autoHideDuration: null,
        actionButtonText: 'Try Again',
        onClick: async () => {
          await onReportableStatusSave();
        }
      });
    }
  };

  const onChangeTagsButtonClick = () => {
    setShowChangeTagsModal(true);
  };

  const onTagsDiscard = () => {
    setUpdatedTags(reportActive.tags ?? undefined);
    setShowChangeTagsModal(false);
  };

  const onTagsChange = (value: FieldValueType) => {
    setUpdatedTags({ ...updatedTags, agency: value as ReportTag[] });
  };

  const onTagsUpdate = async () => {
    setIsLoading(true);
    try {
      const updatedReport = await updateReportTags(
        reportActive.general_information.incident_number as string,
        updatedTags
      );

      onUpdateReportableStatus(updatedReport);
      setShowChangeTagsModal(false);
      setIsLoading(false);
      dispatchMainAlertEvent({
        message: 'Report tags updated successfully',
        autoHideDuration: 3600
      });
    } catch (error) {
      console.error('ReportCard::Error updating tags:', error);
      setIsLoading(false);
      dispatchMainAlertEvent({
        message: 'The report tags could not be updated correctly.',
        autoHideDuration: 3600
      });
    }
  };

  const isAmendDisabled: boolean =
    !showAmend || !canAmendReport(report, user, customer);

  const areTagsUnchanged = React.useMemo(() => {
    return JSON.stringify(updatedTags) === JSON.stringify(reportActive.tags);
  }, [updatedTags, reportActive.tags]);

  const isSaveButtonDisabled =
    (showReportableStatusModal &&
      reportableStatus === intialReportableStatus.current) ||
    (showChangeTagsModal && areTagsUnchanged) ||
    isLoading;

  const isChangeTagsDisabled =
    !canAmendReport(report, user, customer) ||
    !checkPermissions(USER_REPORTS_TAGS_UPDATE, user?.role?.permissions);

  const mapCoords =
    reportActive.general_information.location.address?.coords ||
    report.general_information.device?.coords;

  const contactSummaryValue = {
    citizens: reportActive.citizens?.length || 0,
    officers: reportActive.officers?.length || 0
  };

  const officerBadgeIds = new Set<string>();
  reportActive.cad_rms?.cad?.units?.forEach((unit) => {
    unit.officers_badge_ids?.forEach((badgeId) => officerBadgeIds.add(badgeId));
  });

  const cadSummaryValue = reportActive.cad_rms
    ? {
        subjects: reportActive.cad_rms.subjects?.length || 0,
        units: reportActive.cad_rms.cad?.units?.length || 0,
        officers: officerBadgeIds.size || 0,
        reportingPerson: reportActive.cad_rms.cad?.caller ? 1 : 0
      }
    : null;

  const areasName = reportActive.general_information.location.areas?.map(
    (area) => area.name
  );

  return {
    isHistoryOpen,
    isLoading,
    isLargeScreen,
    reportActive,
    selectedItem,
    customer,
    isAmendDisabled,
    isSaveButtonDisabled,
    isChangeTagsDisabled,
    showReportableStatusModal,
    showChangeTagsModal,
    reportableStatus,
    updatedTags,
    onViewHistory,
    onReportHistoryClick,
    onAmend,
    onSeeFullReport,
    onHistoryPanelClose,
    onHistoryPanelBack,
    onHistoryPanelSave,
    onReportableStatusButtonClick,
    onReportableStatusChange,
    onReportableStatusDiscard,
    onReportableStatusSave,
    onChangeTagsButtonClick,
    onTagsDiscard,
    onTagsUpdate,
    onTagsChange,
    mapCoords,
    contactSummaryValue,
    cadSummaryValue,
    areasName
  };
};
